import { CheckCircle2, MessageCircle } from "lucide-react";
import { SectionHeading } from "./SectionHeading";
import { MagneticButton } from "./MagneticButton";
import { Reveal } from "./Reveal";
import { WA_LINKS } from "../lib/utils";

const PRICING_MANAGEMENT = [
{
  label: "Tenant Placement",
  price: "1 Month's Rent",
  period: "one-time",
  desc: "Full tenant sourcing, screening, background verification, and lease execution.",
  features: ["Tenant background & income verification", "Market-rate rental benchmarking", "Legally vetted lease agreement", "Property condition documentation", "Key handover management"],
  highlight: false
},
{
  label: "Ongoing Management",
  price: "9%",
  period: "of monthly rent collected",
  desc: "Comprehensive month to month management with full transparency and reporting.",
  features: ["Rent collection & disbursement", "Monthly performance summaries", "Video updates from on-ground", "Maintenance coordination", "Legal & compliance support", "Property analytics dashboard"],
  highlight: true
},
{
  label: "Comprehensive Flat Fee",
  price: "Custom",
  period: "after feasibility discussion",
  desc: "Tailored for complex portfolios, unique property types, or full-scope requirements.",
  features: ["All Ongoing Management inclusions", "POA & legal document management", "Multi-property consolidation", "Priority response SLA", "Dedicated account manager", "Customised reporting cadence"],
  highlight: false
}];


export function PricingSection() {
  return (
    <section id="pricing" className="relative py-32 bg-stone-50 overflow-hidden" data-testid="pricing-section">
      <div className="pointer-events-none absolute inset-0 -z-10 opacity-30" style={{ backgroundImage: `radial-gradient(circle, #0A254018 1px, transparent 1px)`, backgroundSize: "28px 28px" }} />
      <div className="container-luxury">
        <SectionHeading eyebrow="Transparent Pricing" title="No Hidden Charges." titleAccent="Ever." subtitle="Clear, performance-aligned pricing so you know exactly what you pay and what you get. GST applicable as per government norms." />

        <div className="mt-20">
          <Reveal><h3 className="text-center text-xs font-semibold uppercase tracking-[0.2em] text-navy-600/60 mb-10">Property Management</h3></Reveal>
          <div className="grid gap-6 lg:grid-cols-3">
            {PRICING_MANAGEMENT.map((tier, i) =>
            <Reveal key={tier.label} delay={i * 0.1}>
                <div className={`relative flex flex-col rounded-2xl p-8 h-full transition-shadow hover:shadow-luxury ${tier.highlight ? "bg-navy-800 text-white shadow-luxury-lg ring-2 ring-emerald-400/30" : "bg-white border border-navy-100"}`}>
                  {tier.highlight &&
                <div className="absolute -top-3.5 left-1/2 -translate-x-1/2">
                      <span className="rounded-full bg-emerald-400 px-4 py-1 text-xs font-semibold text-navy-900">Most Popular</span>
                    </div>
                }
                  <div className="mb-8">
                    <p className={`text-xs font-semibold uppercase tracking-[0.18em] ${tier.highlight ? "text-emerald-300" : "text-navy-500/70"}`}>{tier.label}</p>
                    <div className="mt-4 flex items-baseline gap-2">
                      <span className={`text-4xl font-bold tracking-tight ${tier.highlight ? "text-white" : "text-navy-800"}`}>{tier.price}</span>
                    </div>
                    <p className={`mt-1 text-sm ${tier.highlight ? "text-white/60" : "text-navy-500/70"}`}>{tier.period}</p>
                    <p className={`mt-4 text-sm leading-relaxed ${tier.highlight ? "text-white/70" : "text-navy-600/70"}`}>{tier.desc}</p>
                  </div>
                  <ul className="flex flex-col gap-3 mb-8">
                    {tier.features.map((f) =>
                  <li key={f} className="flex items-start gap-2.5 text-sm">
                        <CheckCircle2 size={15} className={`mt-0.5 shrink-0 ${tier.highlight ? "text-emerald-300" : "text-emerald-500"}`} />
                        <span className={tier.highlight ? "text-white/80" : "text-navy-700/80"}>{f}</span>
                      </li>
                  )}
                  </ul>
                  <div className="mt-auto">
                    <MagneticButton href={WA_LINKS.healthCheck} external size="md" variant={tier.highlight ? "secondary" : "primary"} className="w-full">Get Started</MagneticButton>
                  </div>
                </div>
              </Reveal>
            )}
          </div>
        </div>

        <Reveal delay={0.2}>
          <div className="mt-12 rounded-2xl border border-navy-100 bg-white p-8 md:p-10 flex flex-col md:flex-row md:items-center md:justify-between gap-8 shadow-sm">
            <div>
              <p className="eyebrow mb-3">Asset Acquisition & Disposition</p>
              <h3 className="text-xl font-semibold text-navy-800 tracking-tight">Buying or Selling a Property?</h3>
              <p className="mt-2 max-w-lg text-sm text-navy-600/70 leading-relaxed">Expert guidance through every stage of buying or selling residential and commercial property, with partnerships with trusted construction companies for 360° support.</p>
              <div className="mt-4 flex items-baseline gap-2">
                <span className="text-3xl font-bold text-navy-800">2%</span>
                <span className="text-sm text-navy-500/70">of transaction value · brokerage fee</span>
              </div>
            </div>
            <MagneticButton href={WA_LINKS.call} external size="lg" variant="primary" icon={<MessageCircle size={18} />} className="shrink-0">Discuss Your Deal</MagneticButton>
          </div>
        </Reveal>

        <Reveal delay={0.3}>
          <p className="mt-8 text-center text-xs text-navy-500/60">* All fees are exclusive of GST, which is applicable as per prevailing government norms.</p>
        </Reveal>
      </div>
    </section>);

}